import { useMemo } from 'react';
import {
  checkProjectionContinuity,
  checkProjectionIdentities,
  ContinuityViolation,
  IdentityViolation,
} from '@shared/calculations/funding/projection/assertProjectionIdentities';

interface IdentityChecksPanelProps {
  projection: ClaimProjection;
}

interface MonthViolations {
  monthArrayIndex: number;
  monthIndex: number;
  violations: IdentityViolation[];
}

export function IdentityChecksPanel({ projection }: IdentityChecksPanelProps) {
  const allViolations = useMemo<MonthViolations[]>(() => {
    const out: MonthViolations[] = [];
    projection.months.forEach((month, i) => {
      const violations = checkProjectionIdentities(month);
      if (violations.length > 0) {
        out.push({ monthArrayIndex: i, monthIndex: month.context.monthIndex, violations });
      }
    });
    return out;
  }, [projection]);

  const continuityViolations = useMemo<ContinuityViolation[]>(
    () => checkProjectionContinuity(projection.months),
    [projection],
  );

  const totalMonths = projection.months.length;
  const failingMonths = allViolations.length;
  const allPass = failingMonths === 0 && continuityViolations.length === 0;

  // All-pass is the common case: a quiet one-line footer rather than a section.
  if (allPass) {
    return (
      <p className="pt-2 text-center text-[11px] text-gray-400">
        Identity checks: all {totalMonths} months pass (incl. continuity)
      </p>
    );
  }

  // Failures still surface their full detail — they signal a real engine bug.
  return (
    <div className="space-y-3 pt-2">
      <p className="text-xs font-medium text-red-600">
        Identity checks: ✗
        {failingMonths > 0 && ` ${failingMonths} of ${totalMonths} months failed`}
        {failingMonths > 0 && continuityViolations.length > 0 && ','}
        {continuityViolations.length > 0 && ` ${continuityViolations.length} continuity breaks`}
      </p>
      {allViolations.map(({ monthArrayIndex, monthIndex, violations }) => (
        <div key={monthArrayIndex} className="rounded border border-red-200 bg-red-50 p-3">
          <div className="text-sm font-medium text-red-800">
            Month {monthArrayIndex} (monthIndex {monthIndex})
          </div>
          <ul className="mt-2 space-y-1 text-xs text-gray-700">
            {violations.map((v, vi) => (
              <li key={vi} className="font-mono">
                <span className="font-semibold">{v.balance}</span>: open={v.open.toFixed(2)},
                expected={v.expectedClose.toFixed(2)}, actual={v.close.toFixed(2)},{' '}
                <span className="text-red-700">Δ={v.delta.toFixed(4)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {continuityViolations.length > 0 && (
        <div className="rounded border border-red-200 bg-red-50 p-3">
          <div className="text-sm font-medium text-red-800">
            Open/close continuity (openState vs prior close)
          </div>
          <ul className="mt-2 space-y-1 text-xs text-gray-700">
            {continuityViolations.map((v, vi) => (
              <li key={vi} className="font-mono">
                months[{v.monthArrayIndex}] <span className="font-semibold">{v.field}</span>:
                expected={JSON.stringify(v.expected)},{' '}
                <span className="text-red-700">actual={JSON.stringify(v.actual)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
